import { useState } from "react";
import type { Definition } from "../../services/types";
import { useReadAgo } from "../../lib/readAgo";

// A row of numbers, each with its definition one click away. Labels come from
// the backend's definitions so the copy has one owner. The open definition
// renders under the grid rather than inside a tile, so the 1px-gap grid never
// misaligns.

export type Tile = { id: string; value: number | null | undefined };

type Props = {
  tiles: Tile[];
  definitions?: Record<string, Definition>;
  readAt?: string | null;
};

const nf = new Intl.NumberFormat("en-US");
const warned = new Set<string>();

export function tileLabel(id: string, definitions?: Record<string, Definition>): string {
  const label = definitions?.[id]?.label;
  if (label) return label;
  if (import.meta.env.DEV && !warned.has(id)) {
    warned.add(id);
    console.warn(`No definition was sent for the tile ${id}; showing its id instead.`);
  }
  return id.split(".").pop() || id;
}

export function StatTiles({ tiles, definitions, readAt }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const readAgo = useReadAgo(readAt);
  const openDef = open ? definitions?.[open] : undefined;

  return (
    <>
      <div className="stats">
        {tiles.map((t) => {
          const isOpen = open === t.id;
          return (
            <div key={t.id} className={`stat${isOpen ? " open" : ""}`}>
              <div className="n">{t.value == null ? "" : nf.format(t.value)}</div>
              <button
                type="button"
                className="l"
                aria-expanded={isOpen}
                aria-controls="defrow"
                onClick={() => setOpen(isOpen ? null : t.id)}
              >
                {tileLabel(t.id, definitions)}
              </button>
            </div>
          );
        })}
      </div>
      {open ? (
        <dl className="defrow" id="defrow">
          <dt>Meaning</dt>
          <dd>{openDef?.meaning || "No definition was sent for this number."}</dd>
          <dt>From</dt>
          <dd>{openDef?.source || "Not stated."}</dd>
          <dt>Computed</dt>
          <dd>{openDef?.computed || "Not stated."}</dd>
          <dt>Read</dt>
          <dd>{readAgo || "Read time not sent."}</dd>
        </dl>
      ) : null}
    </>
  );
}
